"use client";

import { hasPreferencesPersistenceConsent } from "@analytics/lib/analytics-consent-gate";
/**
 * Client-side persistence for the **preferences** CMP category only.
 *
 * Keys here must stay aligned with cookie policy copy: theme and cursor styling
 * are optional UI preferences. Do not add SaaS gameplay, account, or landing-demo
 * state — those are outside this tier (see e.g. `skillquest:view-settings:v1`,
 * player action queues, `sq.landingDemo.v1`).
 *
 * Reads/writes are no-ops when {@link hasPreferencesPersistenceConsent} is false.
 * {@link clearPreferenceStorage} is invoked from the consent provider on revoke.
 *
 * Topic learner reading pace (`sq-topic-reading-pace`), autoplay on/off (`sq-topic-autoplay`),
 * default topic format (`sq-topic-format`), timeline scope (`sq-topic-timeline-scope`),
 * playback volume (`sq-topic-playback-volume`), battle sim speed (`sq-battle-sim-speed`),
 * and topic captions preferences are stored here when preferences consent is granted.
 */
import type { CaptionHighlightMode } from "@repo/api/modules/learn/contracts/topic-narration";
import { captionHighlightModeSchema } from "@repo/api/modules/learn/contracts/topic-narration";

/**
 * Canonical theme key for `next-themes` **and** this module — must stay identical to
 * `storageKey` on `ThemeProvider` in `ConsentAwareThemeProvider`. Never pass
 * `storageKey={undefined}` to `ThemeProvider`: next-themes defaults to
 * {@link NEXT_THEMES_DEFAULT_THEME_STORAGE_KEY}.
 */
export const PREFERENCES_THEME_LOCAL_STORAGE_KEY = "sq-theme";

/** Default key next-themes uses when `storageKey` is omitted (must be cleared). */
export const NEXT_THEMES_DEFAULT_THEME_STORAGE_KEY = "theme";

const CURSOR_STORAGE_KEY = "sq-cursor-style";

/** Saved topic autoplay reading pace (Radix slider position 0 = slower … 100 = faster, default 50). */
export const PREFERENCES_TOPIC_READING_PACE_LOCAL_STORAGE_KEY =
	"sq-topic-reading-pace";

/** Topic learner autoplay enabled (`"true"` / `"false"`). */
export const PREFERENCES_TOPIC_AUTOPLAY_LOCAL_STORAGE_KEY = "sq-topic-autoplay";

/** Topic learner default format (`"text"` / `"audio"`). */
export const PREFERENCES_TOPIC_FORMAT_LOCAL_STORAGE_KEY = "sq-topic-format";

/** Topic learner deck/doc view mode (`"deck"` / `"doc"`), text format only. */
export const PREFERENCES_TOPIC_VIEW_MODE_LOCAL_STORAGE_KEY =
	"sq-topic-view-mode";

/** Simulated topic audio timeline position by stop id (JSON map of stopId → ms). */
export const PREFERENCES_TOPIC_PLAYBACK_POSITION_LOCAL_STORAGE_KEY =
	"sq-topic-playback-position";

/** Topic audio playback rate multiplier (e.g. `1`, `1.25`). */
export const PREFERENCES_TOPIC_PLAYBACK_RATE_LOCAL_STORAGE_KEY =
	"sq-topic-playback-rate";

/** Battle End Turn simulation playback speed (e.g. `1`, `1.5`, `2.5`). */
export const PREFERENCES_BATTLE_SIM_SPEED_LOCAL_STORAGE_KEY =
	"sq-battle-sim-speed";

/** Topic audio playback volume (0–100). */
export const PREFERENCES_TOPIC_PLAYBACK_VOLUME_LOCAL_STORAGE_KEY =
	"sq-topic-playback-volume";

/** Topic learner timeline scrub scope (`"card"` / `"segmented"`). */
export const PREFERENCES_TOPIC_TIMELINE_SCOPE_LOCAL_STORAGE_KEY =
	"sq-topic-timeline-scope";

/** Per-format timeline scope keys (Phase 2). */
export const PREFERENCES_TOPIC_TIMELINE_SCOPE_TEXT_LOCAL_STORAGE_KEY =
	"sq-topic-timeline-scope:text";
export const PREFERENCES_TOPIC_TIMELINE_SCOPE_AUDIO_LOCAL_STORAGE_KEY =
	"sq-topic-timeline-scope:audio";
export const PREFERENCES_TOPIC_TIMELINE_SCOPE_VIDEO_LOCAL_STORAGE_KEY =
	"sq-topic-timeline-scope:video";

export type TopicTimelineScopePreference = "card" | "segmented";

/** Topic docs document summary sidebar collapsed (`"true"` / `"false"`). */
export const PREFERENCES_DOCUMENT_SUMMARY_COLLAPSED_LOCAL_STORAGE_KEY =
	"sq-document-summary-collapsed";

/** Topic audio captions view enabled (`"true"` / `"false"`). */
export const PREFERENCES_TOPIC_CAPTIONS_VIEW_LOCAL_STORAGE_KEY =
	"sq-topic-captions-view";

/** Topic caption karaoke highlight granularity. */
export const PREFERENCES_TOPIC_CAPTION_HIGHLIGHT_MODE_LOCAL_STORAGE_KEY =
	"sq-topic-caption-highlight-mode";

/** Show only the active highlighted caption span (`"true"` / `"false"`). */
export const PREFERENCES_TOPIC_CAPTION_SHOW_ONLY_HIGHLIGHTED_LOCAL_STORAGE_KEY =
	"sq-topic-caption-show-only-highlighted";

export const DEFAULT_TOPIC_CAPTION_HIGHLIGHT_MODE: CaptionHighlightMode =
	"line";

export function readThemePreference(): string | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		return window.localStorage.getItem(PREFERENCES_THEME_LOCAL_STORAGE_KEY);
	} catch {
		return null;
	}
}

export function writeThemePreference(theme: string): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(PREFERENCES_THEME_LOCAL_STORAGE_KEY, theme);
	} catch {
		// Storage unavailable.
	}
}

export function clearThemePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(PREFERENCES_THEME_LOCAL_STORAGE_KEY);
		window.localStorage.removeItem(NEXT_THEMES_DEFAULT_THEME_STORAGE_KEY);
	} catch {
		// Storage unavailable.
	}
}

export function readCursorPreference(): "browser" | "custom" | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(CURSOR_STORAGE_KEY);
		if (raw === "browser" || raw === "custom") {
			return raw;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeCursorPreference(style: "browser" | "custom"): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(CURSOR_STORAGE_KEY, style);
	} catch {
		// Storage unavailable.
	}
}

export function clearCursorPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(CURSOR_STORAGE_KEY);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicReadingPacePreference(): number | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_READING_PACE_LOCAL_STORAGE_KEY,
		);
		if (raw == null || raw.trim() === "") {
			return null;
		}
		const n = Number.parseInt(raw, 10);
		if (!Number.isFinite(n) || n < 0 || n > 100) {
			return null;
		}
		return n;
	} catch {
		return null;
	}
}

export function writeTopicReadingPacePreference(value: number): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	const clamped = Math.min(100, Math.max(0, Math.round(value)));
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_READING_PACE_LOCAL_STORAGE_KEY,
			String(clamped),
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicReadingPacePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_READING_PACE_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicAutoplayPreference(): boolean | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_AUTOPLAY_LOCAL_STORAGE_KEY,
		);
		if (raw === "true") {
			return true;
		}
		if (raw === "false") {
			return false;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicAutoplayPreference(enabled: boolean): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_AUTOPLAY_LOCAL_STORAGE_KEY,
			enabled ? "true" : "false",
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicAutoplayPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_AUTOPLAY_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export type TopicFormatPreference = "text" | "audio" | "video";

export type TopicViewModePreference = "deck" | "doc" | "narration" | "video";

export const TOPIC_VIEW_MODE_PREFERENCES = [
	"deck",
	"doc",
	"narration",
	"video",
] as const satisfies readonly TopicViewModePreference[];

export function isTopicViewModePreference(
	value: string | null | undefined,
): value is TopicViewModePreference {
	return (
		value === "deck" ||
		value === "doc" ||
		value === "narration" ||
		value === "video"
	);
}

const TOPIC_TIMELINE_SCOPE_KEY_BY_FORMAT: Record<
	TopicFormatPreference,
	string
> = {
	text: PREFERENCES_TOPIC_TIMELINE_SCOPE_TEXT_LOCAL_STORAGE_KEY,
	audio: PREFERENCES_TOPIC_TIMELINE_SCOPE_AUDIO_LOCAL_STORAGE_KEY,
	video: PREFERENCES_TOPIC_TIMELINE_SCOPE_VIDEO_LOCAL_STORAGE_KEY,
};

function parseTopicTimelineScopePreference(
	raw: string | null,
): TopicTimelineScopePreference | null {
	if (raw === "card" || raw === "segmented") {
		return raw;
	}
	return null;
}

export function readTopicFormatPreference(): TopicFormatPreference | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_FORMAT_LOCAL_STORAGE_KEY,
		);
		if (raw === "text" || raw === "audio" || raw === "video") {
			return raw;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicFormatPreference(
	format: TopicFormatPreference,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_FORMAT_LOCAL_STORAGE_KEY,
			format,
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicFormatPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_FORMAT_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicViewModePreference(): TopicViewModePreference | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_VIEW_MODE_LOCAL_STORAGE_KEY,
		);
		if (isTopicViewModePreference(raw)) {
			return raw;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicViewModePreference(
	mode: TopicViewModePreference,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_VIEW_MODE_LOCAL_STORAGE_KEY,
			mode,
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicViewModePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_VIEW_MODE_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function topicRemixPreferenceStorageKey(topicId: string): string {
	return `sq-topic-remix-${topicId}`;
}

export function readTopicRemixPreference(topicId: string): string | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			topicRemixPreferenceStorageKey(topicId),
		);
		return raw?.trim() ? raw : null;
	} catch {
		return null;
	}
}

export function writeTopicRemixPreference(
	topicId: string,
	mediaRemixId: string,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			topicRemixPreferenceStorageKey(topicId),
			mediaRemixId,
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicRemixPreference(topicId: string): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(topicRemixPreferenceStorageKey(topicId));
	} catch {
		// Storage unavailable.
	}
}

function readTopicPlaybackPositionMap(): Record<string, number> {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return {};
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_PLAYBACK_POSITION_LOCAL_STORAGE_KEY,
		);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw) as Record<string, unknown>;
		const result: Record<string, number> = {};
		for (const [stopId, value] of Object.entries(parsed)) {
			if (
				typeof value === "number" &&
				Number.isFinite(value) &&
				value >= 0
			) {
				result[stopId] = value;
			}
		}
		return result;
	} catch {
		return {};
	}
}

function writeTopicPlaybackPositionMap(map: Record<string, number>): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_PLAYBACK_POSITION_LOCAL_STORAGE_KEY,
			JSON.stringify(map),
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicPlaybackPositionMs(stopId: string): number | null {
	const map = readTopicPlaybackPositionMap();
	const value = map[stopId];
	return typeof value === "number" ? value : null;
}

export function writeTopicPlaybackPositionMs(
	stopId: string,
	positionMs: number,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	const map = readTopicPlaybackPositionMap();
	map[stopId] = Math.max(0, Math.round(positionMs));
	writeTopicPlaybackPositionMap(map);
}

export function clearTopicPlaybackPositionForStop(stopId: string): void {
	if (typeof window === "undefined") {
		return;
	}
	const map = readTopicPlaybackPositionMap();
	delete map[stopId];
	writeTopicPlaybackPositionMap(map);
}

export function readTopicPlaybackRate(): number | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_PLAYBACK_RATE_LOCAL_STORAGE_KEY,
		);
		if (raw == null || raw.trim() === "") {
			return null;
		}
		const rate = Number.parseFloat(raw);
		if (!Number.isFinite(rate) || rate <= 0 || rate > 4) {
			return null;
		}
		return rate;
	} catch {
		return null;
	}
}

export function writeTopicPlaybackRate(rate: number): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	const clamped = Math.min(4, Math.max(0.5, rate));
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_PLAYBACK_RATE_LOCAL_STORAGE_KEY,
			String(clamped),
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicPlaybackRatePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_PLAYBACK_RATE_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

const BATTLE_SIM_SPEED_PREF_MIN = 0.5;
const BATTLE_SIM_SPEED_PREF_MAX = 3;

function clampBattleSimSpeedPreference(value: number): number {
	if (!Number.isFinite(value)) {
		return 1;
	}
	return Math.min(
		BATTLE_SIM_SPEED_PREF_MAX,
		Math.max(BATTLE_SIM_SPEED_PREF_MIN, value),
	);
}

export function readBattleSimSpeedPreference(): number | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_BATTLE_SIM_SPEED_LOCAL_STORAGE_KEY,
		);
		if (raw == null || raw.trim() === "") {
			return null;
		}
		const rate = Number.parseFloat(raw);
		if (!Number.isFinite(rate)) {
			return null;
		}
		return clampBattleSimSpeedPreference(rate);
	} catch {
		return null;
	}
}

export function writeBattleSimSpeedPreference(speed: number): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	const clamped = clampBattleSimSpeedPreference(speed);
	try {
		window.localStorage.setItem(
			PREFERENCES_BATTLE_SIM_SPEED_LOCAL_STORAGE_KEY,
			String(clamped),
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearBattleSimSpeedPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_BATTLE_SIM_SPEED_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicPlaybackVolume(): number | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_PLAYBACK_VOLUME_LOCAL_STORAGE_KEY,
		);
		if (raw == null || raw.trim() === "") {
			return null;
		}
		const volume = Number.parseInt(raw, 10);
		if (!Number.isFinite(volume) || volume < 0 || volume > 100) {
			return null;
		}
		return volume;
	} catch {
		return null;
	}
}

export function writeTopicPlaybackVolume(volume: number): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	const clamped = Math.min(100, Math.max(0, Math.round(volume)));
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_PLAYBACK_VOLUME_LOCAL_STORAGE_KEY,
			String(clamped),
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicPlaybackVolumePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_PLAYBACK_VOLUME_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicTimelineScopePreferenceForFormat(
	format: TopicFormatPreference,
): TopicTimelineScopePreference | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const keyed = window.localStorage.getItem(
			TOPIC_TIMELINE_SCOPE_KEY_BY_FORMAT[format],
		);
		const keyedScope = parseTopicTimelineScopePreference(keyed);
		if (keyedScope != null) {
			return keyedScope;
		}
		const legacy = window.localStorage.getItem(
			PREFERENCES_TOPIC_TIMELINE_SCOPE_LOCAL_STORAGE_KEY,
		);
		const legacyScope = parseTopicTimelineScopePreference(legacy);
		if (legacyScope != null) {
			writeTopicTimelineScopePreferenceForFormat(format, legacyScope);
			return legacyScope;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicTimelineScopePreferenceForFormat(
	format: TopicFormatPreference,
	scope: TopicTimelineScopePreference,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			TOPIC_TIMELINE_SCOPE_KEY_BY_FORMAT[format],
			scope,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicTimelineScopePreference(): TopicTimelineScopePreference | null {
	return readTopicTimelineScopePreferenceForFormat("audio");
}

export function writeTopicTimelineScopePreference(
	scope: TopicTimelineScopePreference,
): void {
	writeTopicTimelineScopePreferenceForFormat("audio", scope);
}

export function clearTopicTimelineScopePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_TIMELINE_SCOPE_LOCAL_STORAGE_KEY,
		);
		for (const key of Object.values(TOPIC_TIMELINE_SCOPE_KEY_BY_FORMAT)) {
			window.localStorage.removeItem(key);
		}
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicPlaybackPositionPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_PLAYBACK_POSITION_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicCaptionsViewPreference(): boolean | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_CAPTIONS_VIEW_LOCAL_STORAGE_KEY,
		);
		if (raw === "true") {
			return true;
		}
		if (raw === "false") {
			return false;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicCaptionsViewPreference(enabled: boolean): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_CAPTIONS_VIEW_LOCAL_STORAGE_KEY,
			enabled ? "true" : "false",
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicCaptionsViewPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_CAPTIONS_VIEW_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicCaptionHighlightModePreference(): CaptionHighlightMode | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_CAPTION_HIGHLIGHT_MODE_LOCAL_STORAGE_KEY,
		);
		const parsed = captionHighlightModeSchema.safeParse(raw);
		return parsed.success ? parsed.data : null;
	} catch {
		return null;
	}
}

export function writeTopicCaptionHighlightModePreference(
	mode: CaptionHighlightMode,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_CAPTION_HIGHLIGHT_MODE_LOCAL_STORAGE_KEY,
			mode,
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicCaptionHighlightModePreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_CAPTION_HIGHLIGHT_MODE_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readTopicCaptionShowOnlyHighlightedPreference():
	| boolean
	| null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_TOPIC_CAPTION_SHOW_ONLY_HIGHLIGHTED_LOCAL_STORAGE_KEY,
		);
		if (raw === "true") {
			return true;
		}
		if (raw === "false") {
			return false;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeTopicCaptionShowOnlyHighlightedPreference(
	enabled: boolean,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_TOPIC_CAPTION_SHOW_ONLY_HIGHLIGHTED_LOCAL_STORAGE_KEY,
			enabled ? "true" : "false",
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearTopicCaptionShowOnlyHighlightedPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_TOPIC_CAPTION_SHOW_ONLY_HIGHLIGHTED_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function readDocumentSummaryCollapsedPreference(): boolean | null {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(
			PREFERENCES_DOCUMENT_SUMMARY_COLLAPSED_LOCAL_STORAGE_KEY,
		);
		if (raw === "true") {
			return true;
		}
		if (raw === "false") {
			return false;
		}
	} catch {
		return null;
	}
	return null;
}

export function writeDocumentSummaryCollapsedPreference(
	collapsed: boolean,
): void {
	if (!hasPreferencesPersistenceConsent() || typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			PREFERENCES_DOCUMENT_SUMMARY_COLLAPSED_LOCAL_STORAGE_KEY,
			collapsed ? "true" : "false",
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearDocumentSummaryCollapsedPreference(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.removeItem(
			PREFERENCES_DOCUMENT_SUMMARY_COLLAPSED_LOCAL_STORAGE_KEY,
		);
	} catch {
		// Storage unavailable.
	}
}

export function clearPreferenceStorage(): void {
	clearThemePreference();
	clearCursorPreference();
	clearTopicReadingPacePreference();
	clearTopicAutoplayPreference();
	clearTopicFormatPreference();
	clearTopicViewModePreference();
	clearTopicPlaybackRatePreference();
	clearBattleSimSpeedPreference();
	clearTopicPlaybackPositionPreference();
	clearTopicPlaybackVolumePreference();
	clearTopicTimelineScopePreference();
	clearTopicCaptionsViewPreference();
	clearTopicCaptionHighlightModePreference();
	clearTopicCaptionShowOnlyHighlightedPreference();
	clearDocumentSummaryCollapsedPreference();
}
